// ════════════════════════════════════════════════════════════════════════
//  Die Begleitleiste der Redaktion
//
//  Sie sitzt im Rahmen, unter dem Kopf und über dem Inhalt, und geht bei einem
//  Seitenwechsel nicht verloren. Kein Overlay, kein eigener Assistent: die
//  Redakteurin arbeitet die ganze Zeit in der WIRKLICHEN Anwendung.
//
//  ZWEI GESTALTEN VON KNOPF, UND DER UNTERSCHIED IST DIE DATENLAGE
//
//  Eine Station an echten Daten (`done !== null`) hat KEINEN Weiter-Knopf: sie
//  hakt sich ab, wenn die Sache vorliegt. Eine Station, die nur angesehen wird,
//  hat einen - denn sonst gäbe es keinen Weg weiter. Hier ist genau EINE
//  gemessen, und das ist die ehrliche Zahl: an einem Lehrgang, dessen Fassungen
//  unveränderlich sind, darf eine Führung nicht zum Üben schreiben lassen.
//
//  „SPÄTER“ IST IMMER DA
//
//  Kein modaler Zwang, keine erzwungene Reihenfolge, kein Klick, der nicht
//  wegführt. Eine Führung, aus der man nicht herauskommt, ist eine Geiselnahme.
// ════════════════════════════════════════════════════════════════════════

import { Link, useLocation } from "react-router-dom";
import { useTour } from "../../../shared/context/TourContext.js";
import { TOUR_WELCOME } from "../lib/tour.js";
import own from "./tour.module.css";
import shell from "../../../shared/components/shell.module.css";

/**
 * Die Anleitung als PDF, neben der App ausgeliefert.
 *
 * Ein Verweis auf `docs/Redaktion-Lektion-anlegen.md` hilft der Redaktion
 * nicht: sie arbeitet im Browser und hat kein Repository. Die Datei entsteht
 * aus genau dieser Markdown-Quelle (`scripts/anleitung.ts`) und liegt in
 * `apps/admin/public/` - ein Wächter hält fest, dass sie zur Quelle passt.
 */
const ANLEITUNG = "/Anleitung-Redaktion.pdf";

export function TourBar() {
  const tour = useTour();
  const { pathname } = useLocation();

  if (!tour || !tour.running) return null;

  const { step, position, route, checklist } = tour;
  // Nur auf der letzten Station: dort steht der Satz, der sie ankündigt.
  const anleitung = step.key === "fertig" ? ANLEITUNG : null;
  // Ein Link auf die Seite, auf der man steht, ist keiner - er sieht nur aus
  // wie ein Weg.
  const ziel = route !== null && route !== pathname ? route : null;
  /** Die letzte Station, und die Führung steht wirklich dort. */
  const ende = step.key === "fertig" && tour.isCurrent;

  return (
    // Die Leiste folgt der Seitenbreite: `page` und `breiteWeit` sind dieselben
    // Klassen, die `Page` an der Route benutzt. Sonst liefe sie auf einem
    // 27-Zöller über die ganze Breite, während die Karte darunter bei 1600px
    // aufhört - und der Bezug zum hervorgehobenen Knopf wäre weg.
    <section
      className={`${shell.page} ${shell.breiteWeit} ${own.bar}`}
      aria-label="Erste Schritte"
    >
      <p className={own.count}>
        Schritt {position.number} von {position.total}
      </p>
      {tour.atStart && <p className={own.welcome}>{TOUR_WELCOME}</p>}

      {/* ALLE Stationen, nicht nur die eine, die dran ist: wer die Führung nach
          dem Abschluss wieder aufruft, landete sonst für immer auf „Fertig“.

          Der Haken ist eine ANZEIGE, kein Kästchen zum Ankreuzen: erledigt wird
          eine Station durch die Sache selbst. */}
      <ol className={own.list}>
        {checklist.map((eintrag) => {
          const offen = eintrag.step.key === step.key;
          return (
            <li key={eintrag.step.key}>
              <button
                type="button"
                className={own.row}
                // Der Zustand gehört in den Namen: ein Haken, den nur das Auge
                // sieht, ist für eine Vorleseausgabe nicht da.
                aria-label={`${eintrag.step.title} – ${eintrag.done ? "erledigt" : "offen"}`}
                {...(offen ? { "aria-current": "step" as const } : {})}
                onClick={() => tour.select(eintrag.step.key)}
              >
                <span className={own.box} data-erledigt={eintrag.done} aria-hidden="true">
                  {eintrag.done ? "✓" : ""}
                </span>
                <span className={own.rowTitle}>
                  {eintrag.number}. {eintrag.step.title}
                </span>
              </button>

              {offen && <p className={own.text}>{step.text}</p>}
            </li>
          );
        })}
      </ol>

      <div className={own.actions}>
        {ziel !== null && (
          <Link to={ziel} className={own.linkButton}>
            Dorthin
          </Link>
        )}
        {anleitung !== null && (
          // Ein echtes `<a download>`: die Anleitung ist eine Datei, die man
          // behält und ausdruckt, kein Ziel in dieser App. `Link` von React
          // Router führte sie durch den Router und damit ins Leere.
          <a href={anleitung} download className={own.linkButton}>
            Anleitung als PDF
          </a>
        )}
        {/* „Abschließen“ nur, wenn die letzte Station auch die laufende ist. Wer
            sie bloß nachliest, während er in der Mitte steht, bekäme sonst einen
            Knopf, der „fertig“ sagt und das Lesezeichen dort stehen lässt, wo es
            war. */}
        {ende ? (
          <button type="button" className={shell.button} onClick={tour.close}>
            Führung abschließen
          </button>
        ) : (
          // Nur auf der laufenden Station: beim Nachlesen einer früheren würde
          // „weiter“ das Lesezeichen zurückziehen.
          step.done === null &&
          tour.isCurrent && (
            <button type="button" className={shell.button} onClick={tour.advance}>
              Angesehen, weiter
            </button>
          )
        )}
        {/* Auf der letzten Station wäre „Später“ neben „abschließen“ derselbe
            Vorgang zweimal: die Leiste geht in beiden Fällen weg. */}
        {!ende && (
          <button
            type="button"
            className={`${shell.buttonQuiet} ${own.later}`}
            onClick={tour.close}
          >
            Später
          </button>
        )}
      </div>
    </section>
  );
}
